import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import DOMPurify from "dompurify";
import {
  AlertTriangle,
  MessageSquare,
  CheckCircle2,
  Clock,
  CornerDownRight,
  Paperclip,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { RichTextEditor, type MentionSuggestion } from "@/components/ui/RichTextEditor";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useCommentDraft } from "@/hooks/useCommentDraft";
import { useAuthStore } from "@/store/auth";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import { useContractorPreview } from "../contractorPreview";
import { useOrderAccess } from "../orderAccess";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import { orderCommentsApi } from "../api/orderComments";
import { uploadErrorMessage } from "@/utils/managedFile";
import { AttachmentList } from "./AttachmentList";
import type { ManagedFileBrief, OrderComment } from "../types";

const MAX_FILE_BYTES = 25 * 1024 * 1024;
/** Plain-text length above which a message is collapsed behind "Pokaż całość". */
const LONG_BODY_CHARS = 420;

/** Mirrors backend HasFridayAccess: internal users always, contractors only with the
 *  Friday flag. Without this gate every contractor viewing their order fires a 403.
 *  W „Widoku podwykonawcy" (podgląd GK) nie pobieramy listy — podwykonawca jej nie ma. */
function useCanListInternalUsers() {
  const { isPreview } = useContractorPreview();
  const allowed = useAuthStore(
    (s) => s.user != null && (s.user.role !== "contractor" || s.user.has_friday_access === true),
  );
  return allowed && !isPreview;
}

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function sanitize(html: string) {
  // "img" is on the list on purpose: pasting a picture into the editor lands as an
  // <img> node, and stripping it made the message render with a silent hole where
  // the sender saw a photo. Same allow-list as the task/dev-issue threads.
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
  });
}

const IMAGE_EXT_RE = /\.(png|jpe?g|gif|webp|bmp|avif|heic)$/i;

/** content_type is unreliable (browsers and mail clients hand us octet-stream),
 *  so the filename decides too. */
function isImageFile(file: ManagedFileBrief): boolean {
  return (
    (file.content_type || "").startsWith("image/") || IMAGE_EXT_RE.test(file.original_filename)
  );
}

function CommentBody({ body, className }: { body: string; className?: string }) {
  const canListUsers = useCanListInternalUsers();
  const { data: allUsers = [] } = useInternalUsers({ enabled: canListUsers });
  const names = allUsers.map((u) => u.full_name);
  if (isHtmlContent(body)) {
    return (
      <div
        className={clsx("rich-text-content text-sm text-[var(--color-text-primary)]", className)}
        dangerouslySetInnerHTML={{ __html: highlightMentionsInHtml(sanitize(body), names) }}
      />
    );
  }
  return (
    <p className={clsx("text-sm text-[var(--color-text-primary)] whitespace-pre-wrap", className)}>
      {highlightMentions(body, names)}
    </p>
  );
}

function plainText(body: string): string {
  if (!isHtmlContent(body)) return body;
  return body.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/** The thread has exactly two sides: the contractor and GK ELEKTIM. Every visual
 *  cue (avatar tint, accent bar, side label) is derived from this. */
type Side = "contractor" | "gk";

const sideOf = (role: string): Side => (role === "contractor" ? "contractor" : "gk");

const SIDE_LABEL: Record<Side, string> = {
  contractor: "Podwykonawca",
  gk: "GK ELEKTIM",
};

const SIDE_AVATAR: Record<Side, string> = {
  contractor: "bg-amber-100 text-amber-800 font-bold",
  gk: "bg-[var(--color-primary-dim)] text-[var(--color-primary)] font-bold",
};

/** The left bar carries STATE, not side. Side is already unmistakable from the
 *  avatar tint and the label, and painting it there too made a thread of unread
 *  contractor messages one solid block of colour. */
function accentFor(critical: boolean, needsAction: boolean): string {
  if (critical) return "border-l-[var(--color-primary)]";
  if (needsAction) return "border-l-amber-400";
  return "border-l-[var(--color-border)]";
}

function dayKey(iso: string) {
  return new Date(iso).toDateString();
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Dzisiaj";
  if (d.toDateString() === yesterday.toDateString()) return "Wczoraj";
  return formatDate(d);
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString("pl", { hour: "2-digit", minute: "2-digit" });
}

function stamp(iso: string | null): string {
  return formatDateTime(iso, "");
}

function Chip({
  tone,
  children,
}: {
  tone: "new" | "critical" | "done" | "muted";
  children: React.ReactNode;
}) {
  const tones: Record<string, string> = {
    new: "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary-dim)]",
    critical: "border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-text-inverse)]",
    done: "border-green-300 text-green-700 bg-green-50",
    muted: "border-[var(--color-border)] text-[var(--color-text-muted)] bg-[var(--color-bg-elevated)]",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 border px-1.5 py-px font-mono text-[10px] font-medium uppercase tracking-wide",
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

interface Props {
  orderId: string;
  readOnly?: boolean;
  /** Wrapper className. Defaults to "mt-8" (single-column layout); pass "" when the
   *  section sits in a sidebar block that supplies its own spacing. */
  className?: string;
  /** Optional muted subtitle shown under the heading (e.g. what the block is for). */
  description?: string;
  /** Max-height class of the scrolling message list. Defaults to "max-h-[60vh]" (sticky
   *  sidebar); an in-flow section passes a tighter cap so the thread scrolls on its own. */
  threadClassName?: string;
}

export function OrderCommentsSection({
  orderId,
  readOnly: readOnlyProp = false,
  className = "mt-8",
  description,
  threadClassName = "max-h-[60vh]",
}: Props) {
  const qc = useQueryClient();
  const { user } = useAuthStore();
  // W podglądzie GK wątek ma wyglądać jak u podwykonawcy: jego strona jest „moja",
  // a „Nieprzyjęte" dotyczy wiadomości GK, nie jego własnych.
  const { isPreview } = useContractorPreview();
  // Grant „Podgląd" = wątek tylko do odczytu; „Zarządzanie" przyjmuje wpisy jak koordynator.
  const { readOnly: accessReadOnly, canManage: accessCanManage } = useOrderAccess();
  const readOnly = readOnlyProp || accessReadOnly;
  const role = isPreview ? "contractor" : user?.role;
  const mySide: Side = role === "contractor" ? "contractor" : "gk";
  // Addressee-side acknowledgement: the thread is a contractor↔ELEKTIM channel,
  // so contractors acknowledge internal comments and the order's coordinator or
  // a director acknowledges contractor comments — never their own side's.
  // (`readOnly` — podgląd GK albo grant „Podgląd" — nie chowa przycisku, tylko go
  // wyłącza, żeby karta wyglądała tak samo jak u osoby z pełnym dostępem.)
  const canAcknowledge = (authorRole: string) =>
    role === "contractor"
      ? authorRole !== "contractor"
      : (role === "coordinator" || role === "director" || accessCanManage) && authorRole === "contractor";
  const canListUsers = useCanListInternalUsers();
  const { data: allUsers = [] } = useInternalUsers({ enabled: canListUsers });

  const mentionSuggestions = useMemo<MentionSuggestion[]>(
    () => allUsers.map((u) => ({ id: u.id, name: u.full_name, role: u.role, is_ceo: u.is_ceo })),
    [allUsers],
  );

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ["order-comments", orderId],
    queryFn: () => orderCommentsApi.list(orderId),
  });

  const [body, setBody, clearBody] = useCommentDraft(`comment-draft-order-${orderId}`);
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [isCritical, setIsCritical] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [createError, setCreateError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showDropZone, setShowDropZone] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const scrolledRef = useRef(false);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Newest message at the bottom (backend orders ascending) — park the thread there
  // on first render so the latest exchange is what the user sees. scrollTop instead
  // of scrollIntoView: it must not drag the whole page.
  useEffect(() => {
    if (scrolledRef.current || !comments.length || !threadRef.current) return;
    const toBottom = () => {
      if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
    };
    toBottom();
    // Photos load after the first paint and push the thread taller, so the first
    // jump lands short of the newest message — repeat once they are in.
    const t = setTimeout(toBottom, 400);
    scrolledRef.current = true;
    return () => clearTimeout(t);
  }, [comments.length]);

  /** Image files pasted/dropped into the editor become message attachments.
   *  Returns true when something was taken over, so the caller can stop the event. */
  const attachImageFiles = (files: FileList | null | undefined) => {
    const images = Array.from(files ?? []).filter((f) => f.type.startsWith("image/"));
    if (!images.length) return false;
    setPendingFiles((prev) => [...prev, ...images]);
    setCreateError("");
    return true;
  };

  const openComposer = () => {
    setShowForm(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const fileIds = pendingFiles.length
        ? (await Promise.all(pendingFiles.map((f) => orderCommentsApi.uploadFile(f)))).map((f) => f.id)
        : [];
      return orderCommentsApi.create({
        order: orderId,
        body,
        is_critical: isCritical,
        mention_ids: mentionIds,
        files: fileIds,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order-comments", orderId] });
      clearBody();
      setMentionIds([]);
      setIsCritical(false);
      setPendingFiles([]);
      setShowForm(false);
      setShowDropZone(false);
      setCreateError("");
      // The new message lands at the bottom of the thread — follow it there.
      setTimeout(() => {
        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
      }, 150);
    },
    onError: (err) => setCreateError(uploadErrorMessage(err, "Nie udało się dodać komentarza.")),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => orderCommentsApi.acknowledge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["order-comments", orderId] }),
  });

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Messages grouped per calendar day so the date header can stay pinned over the
  // whole day's exchange while the thread scrolls.
  const dayGroups = useMemo(() => {
    const groups: { key: string; label: string; items: OrderComment[] }[] = [];
    for (const c of comments) {
      const key = dayKey(c.created_at);
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.items.push(c);
      else groups.push({ key, label: dayLabel(c.created_at), items: [c] });
    }
    return groups;
  }, [comments]);

  const waitingForMe = comments.filter(
    (c) => c.status === "posted" && canAcknowledge(c.author_role),
  ).length;
  const criticalOpen = comments.filter((c) => c.is_critical && c.status === "posted").length;

  if (isLoading) return null;

  const renderMessage = (comment: OrderComment) => {
    const side = sideOf(comment.author_role);
    const isMine = side === mySide;
    // acknowledged_by_name is checked too: a row that already carries a receipt must
    // never also offer the button, whatever its status field says.
    const needsMyAck =
      comment.status === "posted" &&
      !comment.acknowledged_by_name &&
      canAcknowledge(comment.author_role);
    const text = plainText(comment.body);
    const isLong = text.length > LONG_BODY_CHARS;
    const isOpen = expanded.has(comment.id);
    const images = comment.files_detail.filter(isImageFile);
    const docs = comment.files_detail.filter((f) => !isImageFile(f));

    return (
      <article
        key={comment.id}
        className={clsx(
          "border-l-[3px] px-3 py-3",
          accentFor(comment.is_critical, needsMyAck),
          comment.is_critical ? "bg-[var(--color-primary-dim)]" : "bg-[var(--color-bg-surface)]",
        )}
      >
        <div className="flex gap-2.5">
          <UserAvatar
            fullName={comment.author_name}
            size="sm"
            initialsClassName={SIDE_AVATAR[side]}
            className="mt-0.5"
          />
          <div className="min-w-0 flex-1">
            {/* Who / which side / when */}
            <div className="mb-1 flex items-baseline gap-x-2 gap-y-0.5 flex-wrap">
              <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                {comment.author_name}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                {SIDE_LABEL[side]}
                {isMine && " · my"}
              </span>
              {/* Delivery state lives here as a single glyph. Spelled out under every
                  message it repeated the same sentence down the whole thread. */}
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                {comment.acknowledged_by_name ? (
                  <CheckCircle2
                    size={13}
                    className="text-green-600"
                    aria-label="Przyjęto do wiadomości"
                  />
                ) : comment.status === "posted" && isMine ? (
                  <Clock
                    size={13}
                    className="text-[var(--color-text-muted)]"
                    aria-label="Oczekuje na potwierdzenie drugiej strony"
                  />
                ) : null}
                <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                  {timeLabel(comment.created_at)}
                </span>
              </span>
            </div>

            {comment.is_critical && (
              <div className="mb-1.5 flex flex-wrap gap-1">
                {comment.is_critical && (
                  <Chip tone="critical">
                    <AlertTriangle size={10} /> Zdarzenie krytyczne
                  </Chip>
                )}
              </div>
            )}

            {/* Message */}
            <div className={clsx("relative", isLong && !isOpen && "max-h-36 overflow-hidden")}>
              <CommentBody body={comment.body} />
              {isLong && !isOpen && (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[var(--color-bg-surface)] to-transparent" />
              )}
            </div>
            {isLong && (
              <button
                type="button"
                onClick={() => toggle(comment.id)}
                className="mt-1 font-mono text-[11px] uppercase tracking-wide text-[var(--color-primary)] hover:underline"
              >
                {isOpen ? "Zwiń" : "Pokaż całość"}
              </button>
            )}

            {/* Photos ride along as attachments (their links are re-signed on every
                read, so unlike a URL baked into the body they never go stale) —
                show them as pictures, not as file rows. */}
            {images.length > 0 && (
              <div className="mt-2 flex flex-wrap items-start gap-1.5">
                {images.map((f) =>
                  f.url ? (
                    <a
                      key={f.id}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                      title={`${f.original_filename} — otwórz w pełnym rozmiarze`}
                      className="block border border-[var(--color-border)] bg-white hover:border-[var(--color-primary)]"
                    >
                      {/* w-auto + object-contain: a logo stays a logo. Stretching every
                          attachment to the column width blew small images up into a
                          blurry slab. */}
                      <img
                        src={f.url}
                        alt={f.original_filename}
                        loading="lazy"
                        className="block h-auto max-h-44 w-auto max-w-full object-contain"
                      />
                    </a>
                  ) : (
                    <span
                      key={f.id}
                      className="flex items-center border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1.5 text-[11px] text-[var(--color-text-muted)]"
                    >
                      {f.original_filename} — plik w trakcie sprawdzania
                    </span>
                  ),
                )}
              </div>
            )}

            {docs.length > 0 && (
              <div className="mt-2">
                <AttachmentList files={docs} />
              </div>
            )}

            {/* Response left when the thread item was resolved */}
            {comment.status === "resolved" && comment.response && (
              <div className="mt-2 border border-green-200 bg-green-50 px-2.5 py-2">
                <p className="mb-0.5 font-mono text-[10px] uppercase tracking-wide text-green-700">
                  Odpowiedź
                </p>
                <CommentBody body={comment.response} />
                {comment.resolved_by_name && (
                  <p className="mt-1 text-[11px] text-green-700">
                    {comment.resolved_by_name} · {stamp(comment.resolved_at)}
                  </p>
                )}
              </div>
            )}

            {/* Who acknowledged and when — the one piece of state worth spelling out;
                everything else is the glyph next to the timestamp. */}
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              {comment.acknowledged_by_name && (
                <span className="text-[11px] text-green-700">
                  Przyjęto: {comment.acknowledged_by_name} · {stamp(comment.acknowledged_at)}
                </span>
              )}

              {/* Quiet by default, loud only where it must be. Every message from the
                  other side needs this button, so a red slab on each one turned the
                  thread into a wall of buttons — a critical event still gets one. */}
              {/* W podglądzie GK (readOnly) przycisk zostaje widoczny, ale wyłączony —
                  podwykonawca go ma, więc podgląd nie może go chować. */}
              {needsMyAck && (
                comment.is_critical ? (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => acknowledgeMutation.mutate(comment.id)}
                    disabled={readOnly}
                    title={readOnly ? "Podgląd — akcje niedostępne" : undefined}
                    loading={acknowledgeMutation.isPending && acknowledgeMutation.variables === comment.id}
                  >
                    <CheckCircle2 size={13} /> Przyjmij do wiadomości
                  </Button>
                ) : (
                  <button
                    type="button"
                    onClick={() => acknowledgeMutation.mutate(comment.id)}
                    title={readOnly ? "Podgląd — akcje niedostępne" : undefined}
                    disabled={readOnly || (acknowledgeMutation.isPending && acknowledgeMutation.variables === comment.id)}
                    className="inline-flex items-center gap-1 border border-[var(--color-border-strong)] px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:opacity-50"
                  >
                    <CheckCircle2 size={11} /> Przyjmij do wiadomości
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className={className}>
      {/* Heading */}
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-[var(--color-text-primary)]">
            <MessageSquare size={18} className="shrink-0" />
            Komentarze i zdarzenia
            <span className="font-mono text-xs font-normal text-[var(--color-text-muted)]">
              ({comments.length})
            </span>
          </h2>
          {description && (
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">{description}</p>
          )}
        </div>
      </div>

      {(waitingForMe > 0 || criticalOpen > 0) && (
        <div className="mb-2 flex flex-wrap gap-1">
          {waitingForMe > 0 && <Chip tone="new">{waitingForMe} do potwierdzenia</Chip>}
          {criticalOpen > 0 && (
            <Chip tone="critical">
              <AlertTriangle size={10} /> {criticalOpen} krytyczne
            </Chip>
          )}
        </div>
      )}

      {/* One thread, one surface — messages are rows inside it, not standalone cards */}
      <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        {comments.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-[var(--color-text-muted)]">
            Brak korespondencji. {readOnly ? "" : "Napisz pierwszą wiadomość poniżej."}
          </p>
        ) : (
          <div
            ref={threadRef}
            className={clsx(threadClassName, "overflow-y-auto divide-y divide-[var(--color-border-muted)]")}
          >
            {dayGroups.map((group) => (
              <div key={group.key}>
                <div className="sticky top-0 z-10 border-b border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {group.label}
                </div>
                <div className="divide-y divide-[var(--color-border-muted)]">
                  {group.items.map(renderMessage)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Composer docked at the bottom of the thread. W podglądzie GK (readOnly) — ten sam
            przycisk, tylko wyłączony: podwykonawca go widzi, więc podgląd też ma go pokazać. */}
        {readOnly && (
          <div className="border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
            <button
              type="button"
              disabled
              title="Podgląd — akcje niedostępne"
              className="flex w-full items-center gap-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-left text-sm text-[var(--color-text-muted)] opacity-60 cursor-not-allowed"
            >
              <CornerDownRight size={14} className="shrink-0" />
              <span className="truncate">Napisz wiadomość… (@ aby oznaczyć osobę)</span>
            </button>
          </div>
        )}
        {!readOnly && (
          <div
            ref={formRef}
            className="border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3"
          >
            {!showForm ? (
              <button
                type="button"
                onClick={openComposer}
                className="flex w-full items-center gap-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-left text-sm text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-text-primary)]"
              >
                <CornerDownRight size={14} className="shrink-0" />
                <span className="truncate">Napisz wiadomość… (@ aby oznaczyć osobę)</span>
                {body.trim() && (
                  <span className="ml-auto shrink-0 font-mono text-[10px] uppercase tracking-wide text-[var(--color-primary)]">
                    szkic
                  </span>
                )}
              </button>
            ) : (
              <>
                {/* Pictures pasted or dropped into the text are pulled out into the
                    attachment list instead of being embedded in the HTML body: an
                    embedded link is a presigned S3 URL that dies within the hour,
                    while an attachment is re-signed on every read of the thread. */}
                <div
                  className="mb-1"
                  onPaste={(e) => {
                    if (attachImageFiles(e.clipboardData?.files)) e.preventDefault();
                  }}
                  onDropCapture={(e) => {
                    if (attachImageFiles(e.dataTransfer?.files)) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  <RichTextEditor
                    value={body}
                    onChange={setBody}
                    disabled={createMutation.isPending}
                    placeholder="Treść wiadomości… (wpisz @ aby oznaczyć)"
                    mentionSuggestions={mentionSuggestions}
                    onMentionIdsChange={setMentionIds}
                  />
                </div>
                {/* One footer bar carries every option, so the composer stays a single
                    control instead of a stack of loose widgets down the sidebar. */}
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setIsCritical((p) => !p)}
                    aria-pressed={isCritical}
                    title="Wyśle SMS do dyrektora i e-mail do podwykonawcy"
                    className={clsx(
                      "inline-flex items-center gap-1 border px-2 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors",
                      isCritical
                        ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-text-inverse)]"
                        : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]",
                    )}
                  >
                    <AlertTriangle size={11} /> Krytyczne
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDropZone((p) => !p)}
                    aria-pressed={showDropZone}
                    title="Dodaj załącznik (zdjęcie możesz też wkleić Ctrl+V)"
                    className={clsx(
                      "inline-flex items-center gap-1 border px-2 py-1 font-mono text-[10px] uppercase tracking-wide transition-colors",
                      showDropZone || pendingFiles.length
                        ? "border-[var(--color-text-primary)] text-[var(--color-text-primary)]"
                        : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]",
                    )}
                  >
                    <Paperclip size={11} /> Załącz
                    {pendingFiles.length > 0 && ` (${pendingFiles.length})`}
                  </button>

                  <div className="ml-auto flex gap-1.5">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setShowForm(false);
                        setCreateError("");
                        setPendingFiles([]);
                        setShowDropZone(false);
                      }}
                    >
                      Anuluj
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!plainText(body).trim()) {
                          setCreateError("Treść wiadomości jest wymagana.");
                          return;
                        }
                        const tooBig = pendingFiles.find((f) => f.size > MAX_FILE_BYTES);
                        if (tooBig) { setCreateError(`Plik „${tooBig.name}” przekracza 25 MB.`); return; }
                        createMutation.mutate();
                      }}
                      loading={createMutation.isPending}
                    >
                      Wyślij
                    </Button>
                  </div>
                </div>

                {(showDropZone || pendingFiles.length > 0) && (
                  <div className="mt-2">
                    <FileDropZone
                      files={pendingFiles}
                      onFilesChange={(f) => { setPendingFiles(f); setCreateError(""); }}
                      label="Przeciągnij pliki lub kliknij — zdjęcie możesz też wkleić (Ctrl+V)"
                      multiple
                    />
                  </div>
                )}

                {createError && (
                  <p className="mt-2 text-xs text-[var(--color-primary)]">{createError}</p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
